import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDatumPassProduct, fetchDatumPassStatus, purchaseDatumPassWithCoins } from '../store/datumPassSlice';
import { fetchWalletBalance } from '../../wallet/store/walletSlice';
import { fetchNotifications } from '../../../store/slices/countersSlice';
import ModalBackground from '../../../shared/components/ModalBackground';
import PurchaseActions from '../../../shared/components/PurchaseActions';
import datumPassCrownImg from '../../../images/datumpass/bp_crown.png';
import '../../shop/styles/purchaseModal.css';
import '../styles/datumPassOffer.css';
import { useTranslation } from 'react-i18next';
import { msg, tr } from '../../../i18n/msg';
import { entityText } from '../../../i18n/entityText';
import { formatDate, formatNumber } from '../../../i18n/format';

const BENEFITS = [
  {
    key: 'autocollect',
    icon: '/clock.png',
    title: msg('datumpass.benefit.autocollect', 'Auto Collect'),
    desc: msg('datumpass.benefit.autocollect_desc', 'Free tickets accumulate while you sleep'),
  },
  {
    key: 'battlepass',
    icon: '/loading_spinner_icon.png',
    title: msg('datumpass.benefit.battlepass', 'BattlePass Premium'),
    desc: msg('datumpass.benefit.battlepass_desc', 'Unlock the golden track this season'),
  },
  {
    key: 'tickets',
    icon: '/tickets_icon.png',
    title: msg('datumpass.benefit.tickets', '2× Free Tickets'),
    desc: msg('datumpass.benefit.tickets_desc', 'Double pending ticket pool'),
  },
  {
    key: 'affiliate',
    icon: '/slider/ton.png',
    title: msg('datumpass.benefit.affiliate', 'Affiliate Program'),
    desc: msg('datumpass.benefit.affiliate_desc', 'Earn GRAM from your invited players'),
  },
  {
    key: 'pvp',
    icon: '/gram.png',
    title: msg('datumpass.benefit.pvp', 'PvP for GRAM'),
    desc: msg('datumpass.benefit.pvp_desc', 'Play Arena and Durak for GRAM stakes'),
  },
];

// Stars: the bot receives successful_payment a moment after the invoice closes.
// TON: the transfer may be indexed a bit later than verify. Refetch the status a few times.
const STATUS_REFRESH_DELAYS_MS = [0, 3000, 10000, 30000];

/**
 * DatumPass offer. GRAM and Stars use the SAME payment flow as every shop item / pack
 * (PurchaseActions → TON Connect + on-chain verify / Telegram invoice → successful_payment):
 * the backend activates the pass only after the provider confirmed the payment.
 * Coins are debited server-side via /v1/datum-pass/purchase.
 */
export default function DatumPassOfferModal({ onClose, onPurchased }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { product, isLoading, error } = useSelector((s) => s.datumPass);
  // Coin balance is already in the store (header Balance keeps it fresh) — no extra request
  const coinBalance = useSelector((s) => s.wallet?.datumCoins);
  const [visible, setVisible] = useState(false);
  const [coinsBusy, setCoinsBusy] = useState(false);

  useEffect(() => {
    dispatch(fetchDatumPassProduct());
    setVisible(true);
  }, [dispatch]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 250);
  };

  /** After a provider-confirmed payment: pull the new status / balances (works after unmount too).
   *  `onPurchased` lets the host screen refetch its own pass-dependent data on the same schedule
   *  (e.g. BattlePass → premium rewards become claimable). */
  const refreshAfterPayment = () => {
    STATUS_REFRESH_DELAYS_MS.forEach((ms) => {
      setTimeout(() => {
        dispatch(fetchDatumPassStatus());
        dispatch(fetchWalletBalance());
        dispatch(fetchNotifications());
        onPurchased?.();
      }, ms);
    });
  };

  const handleCoinsPurchase = async () => {
    if (!product || coinsBusy) return;
    setCoinsBusy(true);
    try {
      await dispatch(purchaseDatumPassWithCoins({
        productId: product.id,
        idempotencyKey: `dp-coins-${product.id}-${Date.now()}`,
      })).unwrap();
      refreshAfterPayment();
      handleClose();
    } catch (e) {
      // error is exposed via slice state
    } finally {
      setCoinsBusy(false);
    }
  };

  // currentPeriodEndsAt is per-user: current expiry + 30 days when the pass is active, else now + 30 days
  const periodEnd = product?.currentPeriodEndsAt
    ? formatDate(product.currentPeriodEndsAt, { day: 'numeric', month: 'short' })
    : '—';
  const coinsPrice = product?.priceCoins != null ? Number(product.priceCoins) : null;
  // Unknown balance (not loaded yet) never blocks the button; the backend re-checks anyway
  const notEnoughCoins = coinsPrice > 0 && coinBalance != null && Number(coinBalance) < coinsPrice;

  return (
    <div className="purchase-modal-overlay" onClick={handleClose}>
      <div
        className={`purchase-modal-sheet dp-sheet ${visible ? 'visible' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalBackground />

        <div className="purchase-modal-header" style={{ position: 'relative' }}>
          <h2 className="purchase-modal-title dp-title">DatumPass</h2>
          <img className="purchase-modal-close" src="/close.png" alt={t('common.close', 'Close')} onClick={handleClose} />
        </div>

        <div className="dp-period-note">{t('datumpass.period_note', '30 days · Active until {{date}}', { date: periodEnd })}</div>

        <ul className="dp-benefits">
          {BENEFITS.map((b) => (
            <li key={b.key} className="dp-benefit">
              <img src={b.icon} alt="" className="dp-benefit-icon" />
              <div>
                <div className="dp-benefit-title">{tr(t, b.title)}</div>
                <div className="dp-benefit-desc">{tr(t, b.desc)}</div>
              </div>
            </li>
          ))}
        </ul>

        {product && (
          <div className="dp-buttons">
            {/* GRAM / Stars — common provider flow, same buttons as shop items */}
            <PurchaseActions
              allowTON={product.priceTon != null}
              allowStars={product.priceStars != null}
              purchaseType="DATUM_PASS"
              itemId={product.id}
              usdPrice={product.priceUsdt}
              priceTON={product.priceTon}
              priceStars={product.priceStars}
              description={product.description
                ? entityText(t, 'datum_pass_product', product.id, 'description', product.description)
                : t('datumpass.item_label', 'DatumPass · 30 days')}
              itemLabel={t('datumpass.item_label', 'DatumPass · 30 days')}
              itemImg={datumPassCrownImg}
              disabled={coinsBusy}
              onPaid={refreshAfterPayment}
              onClose={handleClose}
            />

            {coinsPrice > 0 && (
              <button
                className="app-btn app-btn--green"
                onClick={handleCoinsPurchase}
                disabled={coinsBusy || isLoading || notEnoughCoins}
                title={notEnoughCoins ? t('datumpass.you_have', 'You have {{n}} coins', { n: formatNumber(coinBalance) }) : undefined}
              >
                {coinsBusy ? t('common.processing', 'Processing…') : (
                  <>
                    {notEnoughCoins
                      ? t('datumpass.not_enough', 'Not enough coins · {{price}}', { price: formatNumber(coinsPrice) })
                      : t('datumpass.pay', 'Pay {{price}}', { price: formatNumber(coinsPrice) })}
                    <img className="app-btn__icon" src="/coin_icon.png" alt={t('common.coins', 'Coins')} />
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {error && <div className="dp-error">{error}</div>}
      </div>
    </div>
  );
}
